<template>
  <StepItem
    format="form"
    :title="pageTitle"
    subtitle="This page is encrypted for your security | Step 1 of 3"
    :loading="loading"
    show-lock
    @handle-next-step="handleNextStep"
    :is-ready-for-next-step="isReadyForNextStep"
  >
    <FormInput
      class="mb-2 w-full"
      required
      label="Company Name / DBA"
      placeholder="e.g. Business Draft"
      v-model="store.name"
      :error="store?.errors?.name"
      @update:modelValue="throttleValidateCurrentStep"
    />
    <FormInputPrefix
      class="my-6 w-full"
      type="text"
      input-mode="url"
      placeholder="example.com"
      format="website"
      :prefixes="['https://', 'http://']"
      default-prefix="https://"
      v-model="store.website"
      label="Company Website"
      label-hint="(Optional)"
      :error="store?.errors?.website"
      @update:modelValue="throttleValidateCurrentStep"
    />
    <FormSingleOrMultiAddresses
      v-model:groupName="store.locationGroupName"
      v-model:locations="store.locations"
      @update:groupName="throttleValidateCurrentStep"
      @update:locations="throttleValidateCurrentStep"
    />
    <FormCheckbox
      v-model="store.enableDefaultSmsNotifications" class="mt-12 mb-4"
      @update:modelValue="validateCurrentStep(false)"
      :error="store?.errors?.enableDefaultSmsNotifications">
      Send time-sensitive notifications to me via text messages (recommended)
    </FormCheckbox>
    <FormCheckbox
      v-model="store.tosChecked"
      :error="store?.errors?.tosChecked"
      @update:modelValue="validateCurrentStep(false)"
    >
      I have read and agree to the
      <a href="/privacy-policy" tabindex="-1" target="_blank"><u>Privacy Policy</u></a>
      and
      <a href="/terms-of-service" tabindex="-1" target="_blank"><u>Terms of Service</u></a
      >, confirm that I am at least 18 years old, and represent that I am authorized to accept these Terms
      on behalf of the company or organization associated with this account.
    </FormCheckbox>
  </StepItem>
</template>

<script setup>
import StepItem from "./StepItem.vue";
import FormInput from "./FormInput.vue";
import FormCheckbox from "./FormCheckbox.vue";
import FormSingleOrMultiAddresses from "./FormSingleOrMultiAddresses.vue";
import FormInputPrefix from "./FormInputPrefix.vue";
import {onActivated, ref, computed, onMounted, watch} from "vue";
import {useCompanyAccountCreateStore} from "../stores/CompanyAccountCreateStore.js";
import {throttle} from "../helpers/DebounceAndThrottle.js";
import {useRoute} from "vue-router";

const emits = defineEmits(["complete"]);

const store = useCompanyAccountCreateStore();
const route = useRoute();

const isReadyForNextStep = ref(false);
const loading = ref(false);

const handleNextStep = async () => {
  if (loading.value) return;
  loading.value = true;

  try {
    await validateCurrentStep(true);
    if (isReadyForNextStep.value) emits("complete", 2);
  } finally {
    loading.value = false;
  }
};

const pageTitle = computed(() => {
  if (route.name === "activate-trial") return "Activate Your 14-Day Free Trial";
  return "Tell us about your company";
});

const validateCurrentStep = async (showToast = false) => isReadyForNextStep.value = await store.validateStep2(showToast);
const throttleValidateCurrentStep = () => throttle(() => validateCurrentStep(false), 100);

watch(() => store.activatingTrial, (isActivating, wasActivating) => {
  if (wasActivating && !isActivating) validateCurrentStep(false);
}, {immediate: true});

onMounted(() => validateCurrentStep(false));
onActivated(() => validateCurrentStep(false));

const handleQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const email = params.get("email");
  if (email) store.email = email;
};

handleQueryParams();
</script>
